import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Palette } from 'lucide-react';
import { BRAND_ORIGIN_TEXT, type BrandingEditState } from '@kaba/shared';
import { api } from '../../lib/api';
import { BrandLogo } from '../BrandLogo';
import { Button } from '../ui/ui';
import { BrandingDialog, type BrandTargetUi } from './BrandingDialog';

/** Kurzübersicht mit Herkunft je Wert und Einstieg in den Dialog (Administration, Team) */
export function BrandingSummary({ target }: { target: Exclude<BrandTargetUi, { scope: 'board' }> }) {
  const [open, setOpen] = useState(false);
  const url = target.scope === 'instance' ? '/api/branding/instance' : `/api/teams/${target.teamId}/branding`;
  const { data } = useQuery({ queryKey: ['branding', target.scope, target.scope === 'team' ? target.teamId : ''], queryFn: () => api.get<BrandingEditState>(url) });
  const r = data?.resolved;
  return (
    <section className="flex flex-col gap-4" aria-labelledby="brand-h">
      <div>
        <h2 id="brand-h" className="text-lg font-semibold">
          Erscheinungsbild
        </h2>
        <p className="text-sm text-[var(--ink-soft)]">
          {target.scope === 'instance'
            ? 'Logo und Markenfarbe für Anmeldung, Übersicht und alle Boards, die nichts Eigenes festlegen.'
            : 'Teamlogo und Teamfarbe für die Boards dieses Teams. Bei nur einem Team genügt meist das Erscheinungsbild der Installation.'}
        </p>
      </div>
      {r && (
        <dl className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-3 rounded-xl border border-[var(--line)] p-4 text-sm">
          <dt className="font-medium">Logo</dt>
          <dd className="flex flex-wrap items-center gap-2">
            {r.logo ? <BrandLogo logo={r.logo} name="Logo" size="preview-desktop" /> : <span>Kein Logo</span>}
            <span className="text-xs text-[var(--ink-soft)]">{BRAND_ORIGIN_TEXT[r.logoOrigin]}</span>
          </dd>
          <dt className="font-medium">Markenfarbe</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <span className="size-6 rounded-full border border-[var(--line)]" style={{ background: r.accent ?? '#3b5bdb' }} aria-hidden />
            <span className="font-mono text-xs">{r.accent ?? 'KaBa-Blau'}</span>
            <span className="text-xs text-[var(--ink-soft)]">{BRAND_ORIGIN_TEXT[r.accentOrigin]}</span>
          </dd>
          <dt className="font-medium">Kopfzeile</dt>
          <dd className="flex flex-wrap items-center gap-2">
            {r.header === 'tinted' ? 'Dezent in Markenfarbe' : 'Neutral'}
            <span className="text-xs text-[var(--ink-soft)]">{BRAND_ORIGIN_TEXT[r.headerOrigin]}</span>
          </dd>
        </dl>
      )}
      {data && (
        <Button className="self-start" variant={data.canEdit ? 'primary' : undefined} onClick={() => setOpen(true)}>
          <Palette size={16} aria-hidden /> {data.canEdit ? 'Erscheinungsbild bearbeiten' : 'Erscheinungsbild ansehen'}
        </Button>
      )}
      <BrandingDialog target={target} open={open} onClose={() => setOpen(false)} />
    </section>
  );
}
